/**
 * Collapsible Stimulus Controller
 *
 * Provides toggle functionality for collapsible UI elements.
 * Handles expanding and collapsing content areas.
 */

import { Controller } from '@hotwired/stimulus';
import application from '~/stimulus_application';

export default class Collapsible extends Controller {
  static targets = ['trigger', 'content', 'chevron', 'header', 'collapsedHeader', 'expandedHeader'];
  static values = { expanded: Boolean };

  declare readonly triggerTarget: HTMLElement;
  declare readonly contentTarget: HTMLElement;
  declare readonly chevronTarget?: HTMLElement;
  declare readonly chevronTargets: HTMLElement[];
  declare readonly headerTarget: HTMLElement;
  declare readonly collapsedHeaderTarget?: HTMLElement;
  declare readonly expandedHeaderTarget?: HTMLElement;
  declare readonly hasCollapsedHeaderTarget: boolean;
  declare readonly hasExpandedHeaderTarget: boolean;
  declare expandedValue: boolean;

  /**
   * Called when the controller is connected to the DOM
   */
  connect() {
    this.updateVisibility();
    this.setupTurboFrameListeners();
  }

  /**
   * Called when the controller is disconnected from the DOM
   */
  disconnect() {
    this.removeTurboFrameListeners();
  }

  /**
   * Toggle the expanded state (chevron action)
   */
  toggle(event?: Event) {
    event?.stopPropagation();
    this.expandedValue = !this.expandedValue;
  }

  /**
   * Open the collapsible if currently closed (header action)
   */
  open(event?: Event) {
    // If the click originated inside the chevron, do nothing (chevron handles toggle/close).
    // This is mainly defensive; toggle() already stops propagation.
    const target = event?.target as Node | null;

    if (target && this.chevronTarget && this.chevronTarget.contains(target)) {
      return;
    }

    if (!this.expandedValue) {
      this.expandedValue = true;
    }
  }

  /**
   * Called when the expanded value changes
   */
  expandedValueChanged() {
    this.updateVisibility();
  }

  /**
   * Update the visibility of the content based on expanded state
   */
  private updateVisibility() {
    if (this.expandedValue) {
      this.expandContent();
      this.rotateChevron(0);
      // Only call showExpandedHeader if both header targets exist
      if (this.hasExpandedHeaderTarget && this.hasCollapsedHeaderTarget) {
        this.showExpandedHeader();
      }
    } else {
      this.collapseContent();
      this.rotateChevron(-90);
      // Only call showCollapsedHeader if both header targets exist
      if (this.hasExpandedHeaderTarget && this.hasCollapsedHeaderTarget) {
        this.showCollapsedHeader();
      }
    }
  }

  /**
   * Expand the content with smooth animation.
   *
   * Reveals the content (removes `hidden`) before animating, so lazy
   * `turbo-frame`s inside become visible and load now only on expand,
   * rather than on initial page render.
   */
  private expandContent() {
    this.contentTarget.classList.remove('max-h-0', 'hidden');
    this.contentTarget.style.maxHeight = `${this.contentTarget.scrollHeight}px`;
    this.liftMaxHeightCap();
  }

  /**
   * Remove the max-height cap once the expand transition finishes, so expanded
   * content can grow freely (e.g. as nested lazy frames load) without clipping.
   */
  private liftMaxHeightCap() {
    const onEnd = (event: TransitionEvent) => {
      if (event.propertyName !== 'max-height') return;
      this.contentTarget.removeEventListener('transitionend', onEnd);
      if (this.expandedValue) this.contentTarget.style.maxHeight = 'none';
    };
    this.contentTarget.addEventListener('transitionend', onEnd);
  }

  /**
   * Collapse the content with smooth animation.
   */
  private collapseContent() {
    // Set explicit height first to enable smooth transition
    this.contentTarget.style.maxHeight = `${this.contentTarget.scrollHeight}px`;
    // Force a reflow to ensure the height is applied before starting the transition
    requestAnimationFrame(() => {
      this.contentTarget.style.maxHeight = '0';
      this.contentTarget.classList.add('max-h-0');
    });
    this.hideContentAfterCollapse();
  }

  /**
   * Add `hidden` once the collapse transition finishes, fully removing the
   * content from layout so lazy frames inside it stay unloaded.
   */
  private hideContentAfterCollapse() {
    const onEnd = (event: TransitionEvent) => {
      if (event.propertyName !== 'max-height') return;
      this.contentTarget.removeEventListener('transitionend', onEnd);
      if (!this.expandedValue) this.contentTarget.classList.add('hidden');
    };
    this.contentTarget.addEventListener('transitionend', onEnd);
  }

  /**
   * Rotate the chevron icon if present
   */
  private rotateChevron(degrees: number) {
    this.chevronTargets.forEach((chevron) => {
      chevron.style.transform = `rotate(${degrees}deg)`;
    });
  }

  /**
   * Show the expanded header and hide the collapsed header
   */
  private showExpandedHeader() {
    if (this.hasExpandedHeaderTarget && this.hasCollapsedHeaderTarget) {
      this.expandedHeaderTarget!.classList.remove('hidden');
      this.collapsedHeaderTarget!.classList.add('hidden');
    }
  }

  /**
   * Show the collapsed header and hide the expanded header
   */
  private showCollapsedHeader() {
    if (this.hasExpandedHeaderTarget && this.hasCollapsedHeaderTarget) {
      this.collapsedHeaderTarget!.classList.remove('hidden');
      this.expandedHeaderTarget!.classList.add('hidden');
    }
  }

  /**
   * Set up listeners for turbo frame load events
   */
  private setupTurboFrameListeners() {
    this.contentTarget.addEventListener('turbo:frame-load', this.handleTurboFrameLoad.bind(this));
  }

  /**
   * Remove turbo frame event listeners
   */
  private removeTurboFrameListeners() {
    this.contentTarget.removeEventListener(
      'turbo:frame-load',
      this.handleTurboFrameLoad.bind(this)
    );
  }

  /**
   * Handle turbo frame load events. When expanded, keep the content uncapped so
   * it grows to fit content that just loaded instead of being clipped.
   */
  private handleTurboFrameLoad() {
    if (this.expandedValue) {
      this.contentTarget.style.maxHeight = 'none';
    }
  }
}

application.register('ui--collapsible', Collapsible);
